import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore, ROLES } from '../store'
import { authAPI } from '../services/api'
import toast from 'react-hot-toast'
import CentinelaLogo from '../components/CentinelaLogo'
import { Building2, Shield, User, ChevronDown, Bell, Upload } from 'lucide-react'

// Lista de hospitales para demo
const DEMO_HOSPITALES = [
  { id: 1, nombre: 'HGZ No. 53 Los Reyes', clave: 'HGZ-53' },
  { id: 2, nombre: 'HGZ No. 57 La Quebrada', clave: 'HGZ-57' },
  { id: 3, nombre: 'HGZ No. 68 Tulpetlac', clave: 'HGZ-68' },
  { id: 4, nombre: 'HGZ No. 76 Xalostoc', clave: 'HGZ-76' },
  { id: 5, nombre: 'HGR No. 196 Ecatepec', clave: 'HGR-196' },
  { id: 6, nombre: 'HGZ No. 197 Texcoco', clave: 'HGZ-197' },
]

// Tipos de rol para el demo
const ROLE_OPTIONS = [
  {
    id: ROLES.ADMIN,
    name: 'Administrador',
    description: 'Acceso total al sistema',
    icon: Shield,
    color: 'green',
    needsHospital: false,
  },
  {
    id: ROLES.OPERADOR,
    name: 'Operador',
    description: 'Gestiona alertas operativas',
    icon: Bell,
    color: 'amber',
    needsHospital: true,
  },
  {
    id: ROLES.HOSPITAL_USER,
    name: 'Carga de Datos',
    description: 'Sube datos al sistema',
    icon: Upload,
    color: 'blue',
    needsHospital: true,
  },
]

export default function Login() {
  const navigate = useNavigate()
  const login = useAuthStore(state => state.login)
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  })
  const [showDemoOptions, setShowDemoOptions] = useState(false)
  const [demoRole, setDemoRole] = useState('admin')
  const [demoHospital, setDemoHospital] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const response = await authAPI.login(formData)
      login(response.data.user, response.data.token)
      toast.success('Bienvenido al sistema')
      navigate('/dashboard')
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al iniciar sesion')
    } finally {
      setLoading(false)
    }
  }

  // Demo login for development - con selección de rol
  const handleDemoLogin = () => {
    const roleConfig = ROLE_OPTIONS.find(r => r.id === demoRole)

    if (roleConfig?.needsHospital && !demoHospital) {
      toast.error('Seleccione un hospital')
      return
    }

    const hospital = DEMO_HOSPITALES.find(h => h.id === demoHospital)

    const getRoleName = () => {
      switch (demoRole) {
        case ROLES.ADMIN: return 'Administrador Central'
        case ROLES.OPERADOR: return `Operador ${hospital?.clave || ''}`
        case ROLES.HOSPITAL_USER: return `Usuario ${hospital?.clave || ''}`
        default: return 'Usuario'
      }
    }

    const userData = {
      id: 1,
      nombre: getRoleName(),
      username: demoRole === ROLES.ADMIN ? 'admin' : `${demoRole}_${hospital?.clave?.toLowerCase() || 'hospital'}`,
      rol: demoRole,
      hospital_id: roleConfig?.needsHospital ? demoHospital : null,
      hospital_nombre: hospital?.nombre || null,
    }

    login(userData, 'demo-token')

    // Mensaje y redirección según rol
    if (demoRole === ROLES.ADMIN) {
      toast.success('Sesion de administrador iniciada')
      navigate('/dashboard')
    } else if (demoRole === ROLES.OPERADOR) {
      toast.success(`Operador de ${hospital?.nombre} - Acceso a Alertas`)
      navigate('/alertas') // Operador va directo a alertas
    } else if (demoRole === ROLES.HOSPITAL_USER) {
      toast.success(`Acceso para carga de datos - ${hospital?.nombre}`)
      navigate('/upload') // Usuario de carga va directo a upload
    } else {
      toast.success(`Sesion iniciada para ${hospital?.nombre}`)
      navigate('/dashboard')
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
      {/* Logo Centinela */}
      <div className="flex flex-col items-center mb-6">
        <CentinelaLogo className="h-20 w-auto mb-2" />
        <h1 className="text-lg font-bold text-green-800 text-center">Sistema de Gestion de Quirofanos</h1>
      </div>

      <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
        Iniciar Sesion
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Usuario
          </label>
          <input
            type="text"
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="Ingrese su usuario"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Contrasena
          </label>
          <input
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="Ingrese su contrasena"
            required
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
        >
          {loading ? 'Ingresando...' : 'Ingresar'}
        </button>
      </form>

      <div className="mt-6 pt-6 border-t border-gray-200">
        <button
          onClick={() => setShowDemoOptions(!showDemoOptions)}
          className="w-full py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
        >
          Entrar en modo Demo
          <ChevronDown className={`w-4 h-4 transition-transform ${showDemoOptions ? 'rotate-180' : ''}`} />
        </button>

        {showDemoOptions && (
          <div className="mt-4 space-y-4 animate-fadeIn">
            {/* Selector de rol */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de usuario
              </label>
              <div className="grid grid-cols-3 gap-2">
                {ROLE_OPTIONS.map((role) => {
                  const Icon = role.icon
                  const isSelected = demoRole === role.id
                  const colorClasses = {
                    green: isSelected ? 'border-green-500 bg-green-50 text-green-700' : '',
                    amber: isSelected ? 'border-amber-500 bg-amber-50 text-amber-700' : '',
                    blue: isSelected ? 'border-blue-500 bg-blue-50 text-blue-700' : '',
                  }
                  return (
                    <button
                      key={role.id}
                      type="button"
                      onClick={() => { setDemoRole(role.id); if (!role.needsHospital) setDemoHospital(null); }}
                      className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                        isSelected
                          ? colorClasses[role.color]
                          : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-xs font-medium">{role.name}</span>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Selector de hospital (si el rol lo requiere) */}
            {ROLE_OPTIONS.find(r => r.id === demoRole)?.needsHospital && (
              <div className="animate-fadeIn">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Seleccionar hospital
                </label>
                <select
                  value={demoHospital || ''}
                  onChange={(e) => setDemoHospital(Number(e.target.value) || null)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="">-- Seleccione un hospital --</option>
                  {DEMO_HOSPITALES.map(hospital => (
                    <option key={hospital.id} value={hospital.id}>
                      {hospital.nombre}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Info del rol seleccionado */}
            <div className={`p-3 rounded-lg text-sm ${
              demoRole === ROLES.ADMIN ? 'bg-green-50 text-green-800' :
              demoRole === ROLES.OPERADOR ? 'bg-amber-50 text-amber-800' :
              'bg-blue-50 text-blue-800'
            }`}>
              {demoRole === ROLES.ADMIN && (
                <p><strong>Administrador:</strong> Acceso total al sistema. Ve todos los hospitales y gestiona todo.</p>
              )}
              {demoRole === ROLES.OPERADOR && (
                <p><strong>Operador:</strong> Gestiona alertas operativas de su hospital (quirofanos, insumos, disponibilidad).</p>
              )}
              {demoRole === ROLES.HOSPITAL_USER && (
                <p><strong>Carga de Datos:</strong> Sube archivos de pacientes, cirugias y datos de su unidad medica.</p>
              )}
            </div>

            {/* Boton de login demo */}
            <button
              onClick={handleDemoLogin}
              className={`w-full py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                demoRole === ROLES.ADMIN ? 'bg-green-600 hover:bg-green-700 text-white' :
                demoRole === ROLES.OPERADOR ? 'bg-amber-600 hover:bg-amber-700 text-white' :
                'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {demoRole === ROLES.ADMIN && <><Shield className="w-5 h-5" /> Entrar como Administrador</>}
              {demoRole === ROLES.OPERADOR && <><Bell className="w-5 h-5" /> Entrar como Operador</>}
              {demoRole === ROLES.HOSPITAL_USER && <><Upload className="w-5 h-5" /> Entrar para Carga de Datos</>}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
